import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { LayoutGrid, Timer, History, Receipt, Trophy, LogOut, Users, DollarSign, LifeBuoy, Repeat, ListOrdered, ShieldCheck, PackagePlus, Menu, X } from "lucide-react";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutGrid, testid: "nav-dashboard" },
  { section: "SERVIZIO" },
  { to: "/turno", label: "Turno di Servizio", icon: Timer, testid: "nav-turno" },
  { to: "/cronologia", label: "Cronologia Turni", icon: History, testid: "nav-cronologia" },
  { to: "/fatture", label: "Fatture", icon: Receipt, testid: "nav-fatture" },
  { to: "/swap", label: "Swap", icon: Repeat, testid: "nav-swap" },
  { to: "/compensi", label: "Stipendi", icon: DollarSign, testid: "nav-compensi" },
  { to: "/classifica", label: "Ore Settimanali", icon: Trophy, testid: "nav-classifica" },
  { to: "/supporto", label: "Supporto", icon: LifeBuoy, testid: "nav-supporto", badgeKey: "openTickets" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [openTickets, setOpenTickets] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    if (!user) return;
    const load = () => api.get("/tickets/count-open").then((r) => setOpenTickets(r.data.count || 0)).catch(() => {});
    load();
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, [user]);

  if (!user) return null;

  const initials = (user.display_name || user.username || "?")
    .split(/[\s._-]+/)
    .map((s) => s[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  const canManage = user?.role === "Proprietario";
  const canApprove = canManage || user?.role === "Supervisore";

  return (
    <div className="min-h-screen w-full flex text-slate-100 relative z-[2]">
      {/* Mobile overlay */}
      {menuOpen && (
        <div
          className="fixed inset-0 bg-black/70 z-40 lg:hidden"
          onClick={() => setMenuOpen(false)}
          data-testid="sidebar-overlay"
        />
      )}
      {/* Sidebar */}
      <aside
        className={`w-64 shrink-0 border-r border-blue-400/20 flex flex-col bg-[#060a1c]/95 fixed inset-y-0 left-0 z-50 transition-transform duration-300 lg:sticky lg:top-0 lg:h-screen lg:translate-x-0 ${menuOpen ? "translate-x-0" : "-translate-x-full"}`}
        data-testid="sidebar"
      >
        <div className="p-6 border-b border-blue-400/15 flex items-center gap-3">
          <button
            onClick={() => setMenuOpen(false)}
            data-testid="close-menu-btn"
            className="lg:hidden absolute top-4 right-4 p-1.5 rounded text-slate-400 hover:text-red-400 transition-colors"
            aria-label="Chiudi menu"
          >
            <X className="w-4 h-4" />
          </button>
          <img src="/logo.png" alt="Los Santos Customs" className="w-11 h-11 rounded-full shrink-0" data-testid="brand-logo" />
          <div>
            <div className="ot-heading text-base font-black tracking-tight uppercase leading-tight">
              <span className="text-blue-400">Los Santos</span>
              <span className="text-[#FFE000]"> Customs</span>
            </div>
            <div className="ot-label !text-[0.6rem] mt-0.5">Officina · Servizio</div>
          </div>
        </div>

        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {NAV.map((item, i) => {
            if (item.section) {
              return (
                <div key={`sec-${i}`} className="ot-label mt-4 mb-1 px-2">{item.section}</div>
              );
            }
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={item.testid}
                onClick={() => setMenuOpen(false)}
                className={({ isActive }) => `ot-nav ${isActive ? "active" : ""}`}
              >
                <Icon className="w-4 h-4" />
                <span className="flex-1">{item.label}</span>
                {item.badgeKey === "openTickets" && canManage && openTickets > 0 && (
                  <span className="ot-mono text-[0.6rem] font-bold bg-amber-400 text-black rounded px-1.5 py-0.5 tracking-widest" data-testid="badge-open-tickets">
                    {openTickets}
                  </span>
                )}
              </NavLink>
            );
          })}
          {canApprove && (
            <>
              <div className="ot-label mt-4 mb-1 px-2">GESTIONE</div>
              <NavLink to="/personale" data-testid="nav-personale" onClick={() => setMenuOpen(false)} className={({ isActive }) => `ot-nav ${isActive ? "active" : ""}`}>
                <Users className="w-4 h-4" />
                <span>Personale</span>
              </NavLink>
              {canManage && (
                <NavLink to="/listino" data-testid="nav-listino" onClick={() => setMenuOpen(false)} className={({ isActive }) => `ot-nav ${isActive ? "active" : ""}`}>
                  <ListOrdered className="w-4 h-4" />
                  <span>Listino</span>
                </NavLink>
              )}
              {canManage && (
                <NavLink to="/import" data-testid="nav-import" onClick={() => setMenuOpen(false)} className={({ isActive }) => `ot-nav ${isActive ? "active" : ""}`}>
                  <PackagePlus className="w-4 h-4" />
                  <span>Import</span>
                </NavLink>
              )}
              {canManage && (
                <NavLink to="/gestione-swap" data-testid="nav-gestione-swap" onClick={() => setMenuOpen(false)} className={({ isActive }) => `ot-nav ${isActive ? "active" : ""}`}>
                  <Repeat className="w-4 h-4" />
                  <span>Gestione Swap</span>
                </NavLink>
              )}
              {canManage && (
                <NavLink to="/ruoli" data-testid="nav-ruoli" onClick={() => setMenuOpen(false)} className={({ isActive }) => `ot-nav ${isActive ? "active" : ""}`}>
                  <ShieldCheck className="w-4 h-4" />
                  <span>Ruoli</span>
                </NavLink>
              )}
            </>
          )}
        </nav>

        <div className="p-4 border-t border-white/5">
          <button
            data-testid="logout-btn"
            onClick={async () => { await logout(); nav("/login"); }}
            className="w-full flex items-center justify-between px-3 py-2.5 rounded border border-red-500/30 text-red-400 hover:bg-red-500/10 transition-colors group"
          >
            <span className="flex items-center gap-2 text-sm font-semibold tracking-wider uppercase ot-heading">
              <LogOut className="w-4 h-4" />
              Termina Sessione
            </span>
            <span className="ot-mono text-[0.65rem] text-red-400/60">V1.0</span>
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0 w-full">
        {/* Topbar */}
        <div className="flex items-center justify-between gap-3 px-4 sm:px-6 lg:px-8 py-4 border-b border-blue-400/15 bg-[#060a1c]/75">
          <button
            onClick={() => setMenuOpen(true)}
            data-testid="open-menu-btn"
            className="lg:hidden p-2 rounded border border-white/10 text-slate-300 hover:text-blue-400 hover:border-blue-400/40 transition-colors"
            aria-label="Apri menu"
          >
            <Menu className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-3 ot-panel px-3 py-2 ml-auto" data-testid="user-badge">
            <div className="text-right">
              <div className="ot-heading font-bold text-sm text-slate-100 leading-tight">{user.display_name || user.username}</div>
              <div className="ot-mono text-[0.65rem] text-blue-400/80 tracking-widest uppercase">{user.role}</div>
            </div>
            <div className="w-9 h-9 rounded-md bg-blue-400/10 border border-blue-400/40 flex items-center justify-center ot-heading font-bold text-blue-400 text-sm">
              {initials}
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="flex-1 w-full px-4 sm:px-6 lg:px-8 py-6 lg:py-8">
          {children}
        </main>
      </div>
    </div>
  );
}
